import { useEffect, useRef, useState } from 'react';

import { graphql } from '@libs/graphql-types';
import { VoiceOverTimingSchema } from '@libs/tts-lib/src';

import { VideoDescriptor } from '@libs/waymark-video/video-descriptor';
import {
  SerializedVideoDescriptor,
  VoiceOverTimingWord,
} from '@libs/waymark-video/video-descriptor-types/src';
import { AnyVideoDescriptor } from '@libs/waymark-video/video-descriptor/src/migrations/types';
import { MigrationContext } from '@libs/waymark-video/video-descriptor/src/migrations/v1_0_2/v1_0_2_migration';

import { apolloClient } from 'shared/api/graphql';
import { VoiceOver } from '@libs/autofill-lib/src';

const USER_VIDEO_VIDEO_DESCRIPTOR_DATA_QUERY = graphql(`
  query UserVideoVideoDescriptorData($userVideoGUID: String!) {
    userVideoByGuid(guid: $userVideoGUID) {
      guid
      videoConfiguration
      videoTemplateVariant {
        id
        videoTemplate {
          id
          slug
        }
      }
      voiceOverText
      voiceOverSpeakerGuid
      voiceOverTiming
    }
  }
`);

export const VIDEO_TEMPLATE_VARIANT_VIDEO_DESCRIPTOR_DATA_QUERY = graphql(`
  query VideoTemplateVariantVideoDescriptorData($videoTemplateVariantSlug: String!) {
    videoTemplateVariantBySlug(slug: $videoTemplateVariantSlug) {
      id
      videoDescriptor
      videoTemplate {
        id
        slug
      }
    }
  }
`);

export const AUTOFILL_RESULT_VIDEO_DESCRIPTOR_DATA_QUERY = graphql(`
  query AutofillResultVideoDescriptorData($autofillResultRequestGUID: String!) {
    autofillResultByRequestGuid(requestGuid: $autofillResultRequestGUID) {
      id
      requestGuid
      videoDescriptor
      voiceover
      userVideo {
        id
        guid
        videoConfiguration
        videoTemplateVariant {
          videoTemplate {
            id
            slug
          }
        }
        voiceOverText
        voiceOverSpeakerGuid
        voiceOverTiming
      }
    }
  }
`);

export const isVideoDescriptor = (obj: unknown): obj is AnyVideoDescriptor =>
  typeof obj === 'object' && obj !== null && 'projectManifest' in obj && 'templateManifest' in obj;

export const loadVideoDescriptor = async ({
  userVideoGUID = null,
  videoTemplateVariantSlug = null,
  autofillResultRequestGUID = null,
}:
  | {
      userVideoGUID: string;
      videoTemplateVariantSlug?: null;
      autofillResultRequestGUID?: null;
    }
  | {
      videoTemplateVariantSlug: string;
      userVideoGUID?: null;
      autofillResultRequestGUID?: null;
    }
  | {
      autofillResultRequestGUID: string;
      userVideoGUID?: null;
      videoTemplateVariantSlug?: null;
    }): Promise<VideoDescriptor> => {
  let videoDescriptor: SerializedVideoDescriptor;
  const migrationContext: MigrationContext = {};

  if (userVideoGUID !== null) {
    const userVideoQueryResult = await apolloClient.query({
      query: USER_VIDEO_VIDEO_DESCRIPTOR_DATA_QUERY,
      variables: { userVideoGUID },
    });

    const userVideo = userVideoQueryResult.data.userVideoByGuid;
    if (!userVideo) {
      throw new Error(
        `Failed to load data while creating VideoDescriptor for user video ${userVideoGUID}`,
      );
    }

    const parsedRawVideoDescriptor = JSON.parse(userVideo.videoConfiguration);
    if (isVideoDescriptor(parsedRawVideoDescriptor)) {
      videoDescriptor = parsedRawVideoDescriptor;
    } else {
      videoDescriptor = {
        templateSlug: userVideo.videoTemplateVariant.videoTemplate.slug,
        configuration: parsedRawVideoDescriptor,
      };
    }

    if (userVideo.voiceOverSpeakerGuid && userVideo.voiceOverText) {
      let timing: VoiceOverTimingWord[] = [];
      try {
        if (userVideo.voiceOverTiming) {
          timing = VoiceOverTimingSchema.parse(JSON.parse(userVideo.voiceOverTiming));
        }
      } catch (err) {
        console.error(
          'Failed to parse voice-over timing for user video. Proceeding with no timing data.',
          err,
        );
      }

      migrationContext.auxiliaryVoiceOverMetadata = {
        speakerGUID: userVideo.voiceOverSpeakerGuid,
        scriptText: userVideo.voiceOverText,
        timing,
      };
    }
  } else if (videoTemplateVariantSlug !== null) {
    const videoTemplateVariantQueryResult = await apolloClient.query({
      query: VIDEO_TEMPLATE_VARIANT_VIDEO_DESCRIPTOR_DATA_QUERY,
      variables: { videoTemplateVariantSlug },
    });

    const videoTemplateVariant = videoTemplateVariantQueryResult.data.videoTemplateVariantBySlug;
    if (!videoTemplateVariant?.videoDescriptor) {
      throw new Error(
        `Failed to load data while creating VideoDescriptor for video template variant ${videoTemplateVariantSlug}`,
      );
    }

    videoDescriptor = JSON.parse(videoTemplateVariant.videoDescriptor);
  } else if (autofillResultRequestGUID !== null) {
    const autofillResultQueryResult = await apolloClient.query({
      query: AUTOFILL_RESULT_VIDEO_DESCRIPTOR_DATA_QUERY,
      variables: { autofillResultRequestGUID },
    });

    const autofillResult = autofillResultQueryResult.data.autofillResultByRequestGuid;
    if (!autofillResult) {
      throw new Error(
        `Failed to load data while creating VideoDescriptor for autofill result ${autofillResultRequestGUID}`,
      );
    }

    const userVideo = autofillResult.userVideo;
    if (userVideo) {
      // If we have a user video saved for this autofill result, prefer to use its data
      const parsedRawVideoDescriptor = JSON.parse(userVideo.videoConfiguration);
      if (isVideoDescriptor(parsedRawVideoDescriptor)) {
        videoDescriptor = parsedRawVideoDescriptor;
      } else {
        videoDescriptor = {
          templateSlug: userVideo.videoTemplateVariant.videoTemplate.slug,
          configuration: parsedRawVideoDescriptor,
        };
      }

      if (userVideo.voiceOverSpeakerGuid && userVideo.voiceOverText) {
        let timing: VoiceOverTimingWord[] = [];
        try {
          if (userVideo.voiceOverTiming) {
            timing = VoiceOverTimingSchema.parse(JSON.parse(userVideo.voiceOverTiming));
          }
        } catch (err) {
          console.error(
            'Failed to parse voice-over timing for user video. Proceeding with no timing data.',
            err,
          );
        }

        migrationContext.auxiliaryVoiceOverMetadata = {
          speakerGUID: userVideo.voiceOverSpeakerGuid,
          scriptText: userVideo.voiceOverText,
          timing,
        };
      }
    } else if (autofillResult.videoDescriptor) {
      videoDescriptor = JSON.parse(autofillResult.videoDescriptor);

      const voiceoverData = autofillResult.voiceover
        ? (JSON.parse(autofillResult.voiceover) as VoiceOver)
        : null;
      if (voiceoverData?.speaker && voiceoverData.voiceOverText) {
        migrationContext.auxiliaryVoiceOverMetadata = {
          speakerGUID: voiceoverData.speaker.id,
          scriptText: voiceoverData.voiceOverText,
          timing: voiceoverData.timing ?? [],
        };
      }
    } else {
      throw new Error(
        `Failed to load video descriptor from autofill result ${autofillResultRequestGUID}`,
      );
    }
  } else {
    throw new Error('No valid parameters provided to loadVideoDescriptor');
  }

  return await VideoDescriptor.from(videoDescriptor, migrationContext);
};

export const useLoadVideoDescriptor = (
  {
    userVideoGUID = null,
    videoTemplateVariantSlug = null,
    autofillResultRequestGUID = null,
  }:
    | {
        userVideoGUID: string | null;
        videoTemplateVariantSlug?: never;
        autofillResultRequestGUID?: never;
      }
    | {
        videoTemplateVariantSlug: string | null;
        userVideoGUID?: never;
        autofillResultRequestGUID?: never;
      }
    | {
        autofillResultRequestGUID: string | null;
        userVideoGUID?: never;
        videoTemplateVariantSlug?: never;
      },
  key = userVideoGUID || videoTemplateVariantSlug || autofillResultRequestGUID || '',
): VideoDescriptor | null => {
  const [videoDescriptor, setVideoDescriptor] = useState<VideoDescriptor | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const previouslyLoadedKeyRef = useRef<string>(key);

  // Reload the video descriptor if the key changes or if the video descriptor is null
  const shouldReloadVideoDescriptor =
    previouslyLoadedKeyRef.current !== key || videoDescriptor === null;

  useEffect(() => {
    if (!shouldReloadVideoDescriptor || isLoading) {
      return;
    }

    if (!userVideoGUID && !videoTemplateVariantSlug && !autofillResultRequestGUID) {
      return;
    }

    setIsLoading(true);

    loadVideoDescriptor({
      userVideoGUID,
      videoTemplateVariantSlug,
      autofillResultRequestGUID,
    } as any)
      .then((loadedVideoDescriptor) => {
        previouslyLoadedKeyRef.current = key;
        setVideoDescriptor(loadedVideoDescriptor);
      })
      .catch((error) => {
        console.error('Failed to load video descriptor:', error);
        setVideoDescriptor(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    autofillResultRequestGUID,
    isLoading,
    key,
    shouldReloadVideoDescriptor,
    userVideoGUID,
    videoTemplateVariantSlug,
  ]);

  // Return null if the video descriptor is stale and about to be reloaded or is currently being loaded.
  // This helps prevent weird content flashes when transitioning to a new video descriptor.
  return shouldReloadVideoDescriptor || isLoading ? null : videoDescriptor;
};
